<template>
  <div class="v2-shipments-page">
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading shipments..."
      />
    </div>

    <template v-else>
      <!-- Empty state (no shipments and no filters applied) -->
      <div v-if="!shipments.length && !isFilterApplied">
        <div class="v2-flex v2-justify-center v2-p-12">
          <v2-card class="v2-text-center v2-p-8">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-shipments-page__empty-icon"
            >
              <line
                x1="22"
                y1="2"
                x2="11"
                y2="13"
              />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <p class="v2-text-muted v2-mt-4">
              There are no shipments yet.
            </p>
          </v2-card>
        </div>
      </div>

      <div v-else>
        <!-- Header -->
        <div class="v2-flex v2-justify-between v2-items-center v2-mb-4">
          <h1 class="v2-heading-sm">
            Shipments
          </h1>
          <!--
            One action stays the plain button it has always been: turning Bulk
            Export into a one-item dropdown would ship a KP-4128 UI change to
            every environment that has only the export flag on, costing existing
            users a click for nothing. The menu appears once there is genuinely
            a choice to make.
          -->
          <div
            v-if="bulkActions.length"
            class="v2-shipments-page__bulk-action"
          >
            <v2-button
              v-if="bulkActions.length === 1"
              variant="outline"
              @click="onSelectBulkAction(bulkActions[0])"
            >
              {{ bulkActions[0].label }}
            </v2-button>
            <v2-dropdown
              v-else
              :key="`bulk-action-${bulkActionKey}`"
              :options="bulkActions"
              placeholder="Bulk Action"
              label-key="label"
              value-key="value"
              @select="onSelectBulkAction"
            />
          </div>
        </div>

        <shipment-bulk-export-modal
          v-if="isBulkShipmentExportEnabled"
          :show="showBulkExport"
          @close="closeBulkExport"
        />

        <v2-unassign-products-wizard
          v-if="isBulkRemoveProductsEnabled"
          :show="showBulkRemoveProducts"
          @close="showBulkRemoveProducts = false"
          @removed="onProductsRemoved"
        />

        <!-- Tabs -->
        <v2-tabs
          :tabs="tabsList"
          :active-tab="currentTab"
          @tab-change="onTabChange"
        />

        <!-- Filters row -->
        <div class="v2-flex v2-items-center v2-gap-3 v2-mb-4 v2-shipments-page__filters">
          <div class="v2-shipments-page__filter-item">
            <v2-dropdown
              :options="entityNames"
              :value="queryParams.entityName"
              :placeholder="isBrand ? 'Retailer' : 'Brand'"
              :searchable="true"
              @select="onSelectName"
              @search="loadEntityNames"
            />
          </div>
          <div class="v2-shipments-page__filter-item">
            <v2-date-range-picker
              :key="`shipment-date-${filtersClearKey}`"
              :value="filterDate"
              placeholder="Shipment date"
              @change="onSelectRange"
            />
          </div>
          <!-- KP-3336: Clear filters — subtle inline action shown only when a
               filter-row selection is active. -->
          <button
            v-if="hasActiveFilters"
            type="button"
            class="v2-shipments-page__clear-filters"
            @click="onClearFilters"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
            Clear filters
          </button>
        </div>

        <!-- Table -->
        <v2-card :no-padding="true">
          <v2-table
            :columns="tableColumns"
            :items="shipments"
            :clickable-rows="true"
            @row-click="openShipmentDetails"
          >
            <template #empty>
              <div class="v2-shipments-page__empty-filtered">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="v2-shipments-page__empty-icon"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                <p class="v2-text-muted v2-mt-2">
                  No shipments for this request.
                  <br>Please, change your filter settings.
                </p>
              </div>
            </template>

            <!-- Column: Shipment ID -->
            <!--
              KP-3336: render the shipment id as a real <a href> pointing at the
              PLG-wrapped shipment URL, so right-click / Cmd-click / middle-click
              open it in a new tab inside the authenticated PLG shell. A plain
              left-click is intercepted and handled as in-app SPA navigation.
            -->
            <template #cell(shipmentSeqId)="{ item }">
              <a
                class="v2-shipments-page__shipment-link"
                :href="shipmentDetailHref(item)"
                @click.stop="onShipmentLinkClick(item, $event)"
              >
                {{ item.shipmentSeqId }}
              </a>
            </template>

            <!-- Column: Shipment Date -->
            <template #cell(createdAt)="{ item }">
              <span class="v2-text-nowrap">
                {{ getDateFromTimestamp(item.createdAt) }}
              </span>
            </template>

            <!-- Column: Retailer -->
            <template #cell(retailerName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.retailerName }}
              </span>
            </template>

            <!-- Column: Brand -->
            <template #cell(brandName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.brandName }}
              </span>
            </template>

            <!-- Column: Status -->
            <template #cell(status)="{ item }">
              <v2-badge :variant="parseStatus(item.status) === 'confirmed' ? 'success' : 'default'">
                {{ item.status }}
              </v2-badge>
            </template>

            <!-- Column: Delivered Date -->
            <template #cell(deliveredDate)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.deliveredDate ? getDateFromTimestamp(item.deliveredDate) : '' }}
              </span>
            </template>

            <!-- Column: Total Units -->
            <template #cell(totalQuantity)="{ item }">
              <span>{{ item.totalQuantity }}</span>
            </template>

            <!-- Column: Total Amount -->
            <template #cell(totalPrice)="{ item }">
              <span class="v2-text-nowrap">
                {{ formatCurrency(item.totalPriceAfterDiscount || item.totalPrice, currency(item)) }}
              </span>
            </template>
          </v2-table>
        </v2-card>

        <!-- Pagination -->
        <v2-pagination
          v-if="hasPagination"
          :current-page="queryParams.page"
          :total-items="totalShipments"
          :per-page="queryParams.limit"
          @page-change="onPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Tabs from '@/components/v2/V2Tabs.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2DateRangePicker from '@/components/v2/V2DateRangePicker.vue'
import { getPlgUrl } from '@/router/routes/utils'
import { useShipmentList } from '@/composables/useShipmentList'
import ShipmentBulkExportModal from '@/views/apps/shipment/ShipmentBulkExportModal.vue'
import { FLAG_CONSTANTS } from '@/constants'
import V2UnassignProductsWizard from './unassign/V2UnassignProductsWizard.vue'

export default {
  name: 'V2ShipmentList',
  components: {
    V2Spinner,
    V2Card,
    V2Tabs,
    V2Table,
    V2Pagination,
    V2Badge,
    V2Dropdown,
    V2Button,
    V2DateRangePicker,
    ShipmentBulkExportModal,
    V2UnassignProductsWizard,
  },
  data() {
    return {
      showBulkExport: false,
      showBulkRemoveProducts: false,
      // Bumped after each pick so the dropdown never keeps a stale selection:
      // it is an action menu, not a value the page holds on to.
      bulkActionKey: 1,
      // KP-3336: this view is kept alive (see LayoutV2). `hasActivated` lets the
      // activated() hook skip the first activation (created() already loaded)
      // and only refresh on later re-entries (e.g. Back from a shipment detail),
      // reusing the retained filters in queryParams.
      hasActivated: false,
      // KP-3336: bumped on Clear filters to remount the date-range picker — its
      // selection is internal to flatpickr, so it doesn't reset from queryParams
      // alone (and re-feeding its :value re-triggers change, which loops).
      filtersClearKey: 0,
    }
  },
  computed: {
    tabsList() {
      return [
        { key: this.TABS.ALL, label: 'All' },
        { key: this.TABS.MARKETPLACE, label: 'Marketplace' },
        { key: this.TABS.DIRECT, label: 'Direct' },
      ]
    },
    // KP-4128: gate the bulk product removal behind its own Flagsmith flag,
    // independent of the export flag so either can ship on its own.
    // Brand-only as well as flag-gated: the endpoint answers 403 to anyone else,
    // and the picker's catch would turn that into a bare "No eligible products
    // found" - a retailer would have no idea why.
    isBulkRemoveProductsEnabled() {
      return (
        this.isBrand
        && !!this.$store.state.flags[
          FLAG_CONSTANTS.KP_4128_BULK_REMOVE_SHIPMENT_PRODUCTS
        ]
      )
    },
    // The menu only lists what the caller can actually do, and the whole
    // control hides when that is nothing - so with both flags off the page
    // looks exactly as it did before.
    bulkActions() {
      const actions = []
      if (this.isBulkShipmentExportEnabled) {
        actions.push({ label: 'Bulk Export', value: 'export' })
      }
      if (this.isBulkRemoveProductsEnabled) {
        actions.push({ label: 'Remove Products', value: 'removeProducts' })
      }
      return actions
    },
    // KP-3377: gate the bulk shipment export behind its Flagsmith flag.
    isBulkShipmentExportEnabled() {
      return this.$store.getters['flags/hasFeature'](
        FLAG_CONSTANTS.KP_3377_BULK_SHIPMENT_EXPORT,
      )
    },
  },
  created() {
    this.loadShipments()
    this.loadEntityNames()
  },
  // KP-3336: kept-alive view. Skip the first activation, then refresh on every
  // Back/return so changes are reflected while the filters stay retained.
  activated() {
    if (!this.hasActivated) {
      this.hasActivated = true
      return
    }
    this.loadShipments()
  },
  methods: {
    openShipmentDetails(item) {
      this.$router.push({
        name: 'v2-shipment/detail',
        params: { shipmentId: item._id },
      })
    },
    // KP-3336: PLG-wrapped shipment URL for the row link's href. Opening in a
    // new tab loads the authenticated PLG shell, which deep-links the iframe to
    // this shipment. Falls back to the in-app B2B route when standalone.
    shipmentDetailHref(item) {
      const plgUrl = getPlgUrl()
      if (plgUrl) return `${plgUrl}/retailer/shipments/shipment/${item._id}`
      return this.$router.resolve({ name: 'v2-shipment/detail', params: { shipmentId: item._id } }).href
    },
    onShipmentLinkClick(item, event) {
      // Modified / middle clicks fall through to the href (new tab in PLG);
      // plain left-click is handled in-app within the current iframe session.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return
      event.preventDefault()
      this.openShipmentDetails(item)
    },
    onTabChange(tab) {
      this.onClickTab(tab)
    },
    // KP-3336: clear the filter-row selections and remount the date-range
    // picker so its internal flatpickr state resets too.
    onClearFilters() {
      this.filtersClearKey += 1
      this.clearFilters()
    },
    onPageChange(page) {
      this.queryParams.page = page
      this.loadShipments()
    },
    onSelectBulkAction(value) {
      const action = typeof value === 'object' ? value?.value : value
      if (action === 'export') {
        this.openBulkExport()
      } else if (action === 'removeProducts') {
        this.showBulkRemoveProducts = true
      }
      this.bulkActionKey += 1
    },
    onProductsRemoved() {
      // Emptied shipments are deleted server-side, so the grid must be refetched
      // rather than patched in place.
      this.loadShipments()
    },
    openBulkExport() {
      this.showBulkExport = true
    },
    closeBulkExport() {
      this.showBulkExport = false
    },
  },
  setup() {
    const shipmentList = useShipmentList()
    return {
      ...shipmentList,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-shipments-page {
  &__filters {
    flex-wrap: wrap;
  }

  &__filter-item {
    min-width: 160px;
    flex: 1;
    max-width: 220px;
  }

  // KP-3336: inline "Clear filters" pill (mirrors the Orders page) — a
  // rounded-full glass pill that tints toward the destructive colour on hover
  // to signal "reset", with a small X spin.
  &__clear-filters {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1-5);
    height: 38px;
    padding: 0 var(--v2-space-3);
    background: var(--v2-glass-surface-1);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-full);
    color: var(--v2-muted-foreground);
    font-family: var(--v2-font-sans);
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.18s ease, border-color 0.18s ease,
      background 0.18s ease, box-shadow 0.18s ease;

    svg {
      flex-shrink: 0;
      opacity: 0.9;
    }

    &:hover {
      color: var(--v2-destructive);
      border-color: var(--v2-destructive);
      background: var(--v2-glass-surface-2);

      svg {
        opacity: 1;
      }
    }

    &:active {
      transform: scale(0.97);
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--v2-ring);
    }
  }

  &__empty-icon {
    color: var(--v2-muted-foreground);
    margin: 0 auto;
    display: block;
  }

  &__empty-filtered {
    text-align: center;
    padding: var(--v2-space-8) var(--v2-space-4);
  }

  &__shipment-link {
    background: none;
    border: none;
    color: inherit;
    font-weight: inherit;
    font-size: inherit;
    font-family: inherit;
    text-decoration: none;
    cursor: pointer;
    padding: 0;
    white-space: nowrap;
  }
}

.v2-text-nowrap {
  white-space: nowrap;
}

.v2-text-center {
  text-align: center;
}

.v2-text-muted {
  color: var(--v2-muted-foreground);
}
</style>
